// Находки взаимоувязки разделов («Коллегия», мигр. 074).
// Кросс-раздельные расхождения не даёт ни одна из четырёх основных проверок:
// комплектность судит раздел по его же чанкам, нормы и решения считаются внутри
// раздела, соответствие заданию сверяет ПД с ТЗ. Здесь — то, что видно только
// при сопоставлении разделов между собой.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Layers } from 'lucide-react';
import { api } from '@/api/client';
import { useMarkup } from '@/hooks/useMarkup';
import { MarkupToggle, MarkupFields } from '@/components/MarkupUI';

interface Evidence { section: string; quote: string; page?: number | null }
interface Finding {
  id: string; code: string; category: string; severity: string;
  title: string; body: string; sections: string[]; evidence: Evidence[];
}
interface Resp { items: Finding[]; total: number; high: number; by_category: Record<string, number> }

const SEV: Record<string, { label: string; fg: string; bg: string }> = {
  high:   { label: 'Требует решения', fg: '#B42318', bg: 'rgba(217,45,32,.08)' },
  medium: { label: 'Существенно',     fg: '#B54708', bg: 'rgba(247,144,9,.10)' },
  low:    { label: 'Редакционное',    fg: '#475467', bg: 'rgba(71,84,103,.08)' },
};

export function CrossSectionPanel({ projectId, showEmpty = false }:
                                  { projectId: string; showEmpty?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  const mk = useMarkup(projectId, 'cross');  // 15.G разметка заказчика по находкам
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['cross-section', projectId],
    queryFn: () => api.get(`/projects/${projectId}/cross-section-findings`),
  });

  if (isLoading) return <div className="text-muted" style={{ fontSize: 13 }}>Загружаю находки…</div>;
  if (!data || data.total === 0) {
    if (!showEmpty) return null;
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center', border: '1px solid #E5E7EB',
                    borderRadius: 10, background: '#F9FAFB' }}>
        <Layers size={22} style={{ color: '#98A2B3' }} />
        <div className="text-ink mt-2" style={{ fontSize: 14, fontWeight: 500 }}>Расхождений между разделами не найдено</div>
        <div className="text-muted mt-1" style={{ fontSize: 12.5 }}>
          Проверка сопоставляет разделы между собой: состав сооружений, обозначения, числовые
          показатели, географические наименования и прослеживание требований задания.
        </div>
      </div>
    );
  }

  const cats = Object.entries(data.by_category)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k}: ${v}`).join(' · ');

  return (
    <section style={{ marginBottom: 28 }}>
      <header style={{ marginBottom: 12 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          <Layers size={16} style={{ color: '#B42318' }} />
          <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
            Взаимоувязка разделов — находок: {data.total}
          </h4>
        </div>
        <p className="text-muted mt-1" style={{ fontSize: 12.5 }}>
          Расхождения между разделами проекта: {cats}. Требуют решения: {data.high}.
        </p>
        <div style={{ marginTop: 10 }}>
          <MarkupToggle on={mk.markupMode} onToggle={() => mk.setMarkupMode((v) => !v)} savedAt={mk.savedAt} />
        </div>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.items.map((f) => {
          const sev = SEV[f.severity] || SEV.medium;
          const isOpen = open === f.id;
          return (
            <article key={f.id} style={{ border: '1px solid #E5E7EB', borderRadius: 10, background: '#fff' }}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : f.id)}
                style={{ width: '100%', textAlign: 'left', padding: '12px 14px', background: 'transparent',
                         border: 0, cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
              >
                <ChevronRight size={15} style={{ marginTop: 2, flexShrink: 0, color: '#98A2B3',
                  transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                <span style={{ flex: 1 }}>
                  <span className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>
                    <span className="tabular text-muted" style={{ marginRight: 8 }}>{f.code}</span>
                    {f.title}
                  </span>
                  <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#667085' }}>
                    {f.sections.join(' · ')}
                  </span>
                </span>
                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 500, padding: '3px 8px',
                               borderRadius: 999, color: sev.fg, background: sev.bg }}>
                  {sev.label}
                </span>
              </button>

              {mk.markupMode && (
                <div style={{ padding: '0 14px 10px 39px' }}>
                  <MarkupFields value={mk.labels[f.id]} onChange={(p) => mk.setLabel(f.id, p)} />
                </div>
              )}
              {isOpen && (
                <div style={{ padding: '0 14px 14px 39px' }}>
                  <div className="text-ink" style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {f.body}
                  </div>
                  {f.evidence.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {f.evidence.map((e, i) => (
                        <blockquote key={i} style={{ margin: 0, padding: '8px 12px', borderLeft: '3px solid #E5E7EB',
                                                     background: '#F9FAFB', borderRadius: 4 }}>
                          <div className="text-muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 3 }}>
                            {e.section}{e.page ? `, стр. ${e.page}` : ''}
                          </div>
                          <div className="text-ink" style={{ fontSize: 12.5, lineHeight: 1.5 }}>«{e.quote}»</div>
                        </blockquote>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
